// components/EngagementPopup.js
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Phone } from 'lucide-react';

const CONVERTED_KEY = 'mb_popup_converted';
const LAST_SHOWN_KEY = 'mb_popup_last_shown';

const EngagementPopup = ({ serviceTitle }) => {
  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);
  const [hasTriggeredThisPage, setHasTriggeredThisPage] = useState(false);

  const resolvedService = serviceTitle || 'Project';

  const canShowNow = () => {
    if (typeof window === 'undefined') return false;

    const converted = localStorage.getItem(CONVERTED_KEY);
    if (converted === 'true') return false;

    const lastShown = localStorage.getItem(LAST_SHOWN_KEY);

    // 10 minutes cooldown
    const cooldown = 10 * 60 * 1000;

    if (lastShown && Date.now() - Number(lastShown) < cooldown) {
      return false;
    }

    return true;
  };

  const triggerPopup = () => {
    if (!canShowNow()) return;

    setIsOpen(true);
    setHasTriggeredThisPage(true);

    // Save globally
    localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
  };

  // Time and scroll triggers
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hasTriggeredThisPage) return;
    if (!canShowNow()) return;

    const timerId = window.setTimeout(() => {
      if (!hasTriggeredThisPage) {
        triggerPopup();
      }
    }, 40000);

    const handleScroll = () => {
      if (hasTriggeredThisPage) return;

      const docElement = document.documentElement;
      const scrollTop =
        window.scrollY || window.pageYOffset || docElement.scrollTop;
      const docHeight = docElement.scrollHeight - window.innerHeight;

      if (docHeight <= 0) return;

      const scrollPercent = scrollTop / docHeight;
      if (scrollPercent >= 0.5) {
        triggerPopup();
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.clearTimeout(timerId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasTriggeredThisPage]);

  const handleClose = () => {
    setIsOpen(false);

    localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
  };

  const handleCallClick = () => {
    // Mark as converted so popup does not keep showing
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(CONVERTED_KEY, 'true');
    }
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-3 sm:px-4">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Close button, small and clean */}
        <button
          onClick={handleClose}
          aria-label="Close popup"
          className="absolute flex h-10 w-10 items-center justify-center rounded-full text-4xl font-semibold text-gray-700 hover:bg-gray-100"
        >
          ×
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left side content */}
          <div className="flex flex-col justify-center px-5 py-6 sm:px-7 sm:py-7">
            <div className="mx-auto w-full max-w-sm text-center">
              <h2 className="mb-3 text-2xl sm:text-3xl font-semibold leading-tight text-black">
                Have Questions About
                <br />
                Your {resolvedService}?
              </h2>

              <p className="mb-4 text-sm sm:text-base text-gray-500 mb-4">
                Get 10% off your project, talk to an expert now.
              </p>

              <div className="flex justify-center mb-4">
                <a
                  href="tel:13233004130"
                  onClick={handleCallClick}
                  className="inline-flex items-center justify-center rounded-full bg-primary px-4 py-2.5 text-sm sm:text-base font-semibold text-white shadow-md hover:opacity-90 w-[210px]"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Call Now & Save 10%
                </a>
              </div>

              <p className="text-[11px] text-gray-400 mb-6">
                No forms, just a quick call to talk through your project.
                <a
                  href="tel:13233004130"
                  onClick={handleCallClick}
                  className="flex items-center justify-center text-[14px] text-gray-400 mt-2"
                >
                +1 (323) 300 4130
                </a>
              </p>
            </div>
          </div>

          {/* Right side image */}
          <div className="h-40 w-full md:h-full">
            <img
              src="/hero/modern-build-home-remodeling-general-contractor-near-me.webp"
              alt="Modern Build Home Remodeling Best General Contractor Near Me"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementPopup;
